import { type AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'

import { type AuthenticationV2Sdk } from './sdks/authenticationV2'
import { type UserSdk } from './sdks/user'
import { canUseWindow } from './utils/canUse'
import { type Configuration, type OutputErrorMessage } from './generated'
import { type AugmentedAxiosRequestConfig } from './interceptors.types'
import { type TrackingSdk } from './tracking'

const isAugmentedAxiosRequestConfig = (config: unknown): config is AugmentedAxiosRequestConfig =>
  (config as AugmentedAxiosRequestConfig).ivts !== undefined

const createRefreshTokensIfNeededRequestInterceptor =
  (userSdk: UserSdk, authenticationV2Sdk: AuthenticationV2Sdk) =>
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    if (isAugmentedAxiosRequestConfig(config) && config.ivts.shouldRefreshTokens && userSdk.isRefreshTokensNeeded()) {
      await authenticationV2Sdk.refreshTokensFromInterceptor()
    }

    return config
  }

const passThroughError = (error: unknown): Promise<unknown> => Promise.reject(error)

export const resetTokenWhenErrorIsTypeOfInvalidJwt =
  (userSdk: UserSdk) =>
  ({ isAxiosError, response }: AxiosError<OutputErrorMessage>): void => {
    if (isAxiosError && response?.status === 401 && response?.data?.type === '/authentication/invalid-jwt-token') {
      userSdk.setInvalidTokenData()
    }
  }

export type AddInterceptorsParams = {
  axios: AxiosInstance
  configuration: Configuration
  userSdk: UserSdk
  trackingSdk: TrackingSdk
  authenticationV2Sdk?: AuthenticationV2Sdk
}

export const addAxiosRequestInterceptors = ({
  axios,
  configuration,
  userSdk,
  trackingSdk,
  authenticationV2Sdk,
}: AddInterceptorsParams): void => {
  // Ajout de la vérification pour ne pas créer l'intercepteur si l'un des paramètres est manquant
  if (authenticationV2Sdk) {
    const refreshTokensIfNeededRequestInterceptor = createRefreshTokensIfNeededRequestInterceptor(
      userSdk,
      authenticationV2Sdk,
    )
    axios.interceptors.request.use(refreshTokensIfNeededRequestInterceptor, passThroughError)
  }

  // Front end url is deduced from basePath in sdk configuration
  const frontEndUrl = configuration?.basePath?.replace(/\/bff$/, '')

  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<OutputErrorMessage>) => {
      if (error.response?.headers['cf-mitigated'] === 'challenge' && canUseWindow()) {
        window.location.href = `${frontEndUrl || ''}/challenge?resumeUrl=${encodeURIComponent(window.location.href)}`

        return new Promise(() => {}) // Keep the promise pending to avoid further processing while redirecting
      }

      return Promise.reject(error)
    },
  )

  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      userSdk.resetConsentIdsIfNecessary(() => trackingSdk.resetConsentIds())

      return response
    },
    (error: AxiosError<OutputErrorMessage>) => {
      resetTokenWhenErrorIsTypeOfInvalidJwt(userSdk)(error)

      throw error
    },
  )
}
